import * as React from "react";
import deepmerge from "deepmerge";
import { Components } from "react-markdown";
import {
  Box,
  Code,
  Heading,
  Text,
  Link,
  Image,
  Separator,
  Table,
  List,
  Checkbox,
} from "@chakra-ui/react-v3";

type GetCoreProps = {
  children?: React.ReactNode;
  "data-sourcepos"?: string;
};

function getCoreProps(props: GetCoreProps): Record<string, unknown> {
  return props["data-sourcepos"] ? { "data-sourcepos": props["data-sourcepos"] } : {};
}

interface Defaults extends Components {
  /**
   * @deprecated Use `h1, h2, h3, h4, h5, h6` instead.
   */
  heading?: Components["h1"];
}

export const defaults: Defaults = {
  p: props => {
    const { children } = props;
    return <Text mb={2}>{children}</Text>;
  },
  em: props => {
    const { children } = props;
    return (
      <Text as="em" fontStyle="italic">
        {children}
      </Text>
    );
  },
  strong: props => {
    const { children } = props;
    return (
      <Text as="strong" fontWeight="bold">
        {children}
      </Text>
    );
  },
  blockquote: props => {
    const { children } = props;
    return (
      <Box
        as="blockquote"
        pl={4}
        py={2}
        borderLeft="4px solid"
        borderColor="border.emphasized"
        bg="bg.subtle"
        fontStyle="italic"
      >
        {children}
      </Box>
    );
  },
  code: props => {
    const { children, className } = props;
    const inline = "inline" in props ? props.inline : false;

    if (inline) {
      return (
        <Code px={1} py={0.5} fontSize="sm" bg="bg.emphasized" color="fg.default" borderRadius="md">
          {children}
        </Code>
      );
    }

    return (
      <Code
        className={className}
        display="block"
        w="full"
        p={4}
        bg="bg.subtle"
        borderRadius="md"
        overflowX="auto"
        fontSize="sm"
        whiteSpace="pre"
      >
        {children}
      </Code>
    );
  },
  del: props => {
    const { children } = props;
    return (
      <Text as="del" textDecoration="line-through">
        {children}
      </Text>
    );
  },
  hr: () => {
    return <Separator my={4} />;
  },
  a: props => {
    const { children, href } = props;
    return (
      <Link
        href={href}
        color="blue.500"
        textDecoration="underline"
        _hover={{
          color: "blue.600",
        }}
        _dark={{
          color: "blue.300",
          _hover: {
            color: "blue.200",
          },
        }}
      >
        {children}
      </Link>
    );
  },
  img: props => {
    const { src, alt } = props;
    return <Image src={src} alt={alt} maxW="full" h="auto" borderRadius="md" my={2} />;
  },
  text: props => {
    const { children } = props;
    return <Text as="span">{children}</Text>;
  },
  ul: props => {
    const { children } = props;
    const attrs = getCoreProps(props);
    const depth = "depth" in props ? props.depth : 0;
    const listStyleType = depth === 1 ? "circle" : "disc";
    return (
      <List.Root
        as="ul"
        pl={6}
        my={2}
        css={{
          listStyleType,
          "& li": {
            display: "list-item",
          },
        }}
        {...attrs}
      >
        {children}
      </List.Root>
    );
  },
  ol: props => {
    const { children } = props;
    const attrs = getCoreProps(props);
    const depth = "depth" in props ? props.depth : 0;
    const listStyleType = depth === 1 ? "lower-alpha" : "decimal";
    return (
      <List.Root
        as="ol"
        pl={6}
        my={2}
        css={{
          listStyleType,
          "& li": {
            display: "list-item",
          },
        }}
        {...attrs}
      >
        {children}
      </List.Root>
    );
  },
  li: props => {
    const { children } = props;
    const attrs = getCoreProps(props);
    const checked = "checked" in props ? props.checked : null;

    if (checked !== null && checked !== undefined) {
      return (
        <List.Item
          display="flex"
          alignItems="flex-start"
          gap={2}
          mb={1}
          css={{ listStyleType: "none" }}
          {...attrs}
        >
          <Checkbox.Root checked={checked} readOnly mt={0.5}>
            <Checkbox.Control />
          </Checkbox.Root>
          <Box flex={1}>{children}</Box>
        </List.Item>
      );
    }

    return (
      <List.Item mb={1} {...attrs}>
        {children}
      </List.Item>
    );
  },
  heading: props => {
    const { children } = props;
    const level = "level" in props && typeof props.level === "number" ? props.level : 1;
    const sizes: Array<"2xl" | "xl" | "lg" | "md" | "sm" | "xs"> = [
      "2xl",
      "xl",
      "lg",
      "md",
      "sm",
      "xs",
    ];
    const headingLevel = Math.min(Math.max(level, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;

    return (
      <Heading
        mt={2}
        mb={4}
        as={`h${headingLevel}`}
        size={sizes[headingLevel - 1] || "md"}
        fontWeight="bold"
        lineHeight="shorter"
        {...getCoreProps(props)}
      >
        {children}
      </Heading>
    );
  },
  pre: props => {
    const { children } = props;
    return (
      <Box
        as="pre"
        p={4}
        bg="bg.subtle"
        borderRadius="md"
        overflowX="auto"
        fontSize="sm"
        whiteSpace="pre"
        {...getCoreProps(props)}
      >
        {children}
      </Box>
    );
  },
  table: props => {
    const { children } = props;
    return (
      <Table.Root variant="outline" my={4} size="sm">
        {children}
      </Table.Root>
    );
  },
  thead: props => {
    const { children } = props;
    return <Table.Header>{children}</Table.Header>;
  },
  tbody: props => {
    const { children } = props;
    return <Table.Body>{children}</Table.Body>;
  },
  tr: props => {
    const { children } = props;
    return <Table.Row>{children}</Table.Row>;
  },
  td: props => {
    const { children } = props;
    return <Table.Cell>{children}</Table.Cell>;
  },
  th: props => {
    const { children } = props;
    return <Table.ColumnHeader>{children}</Table.ColumnHeader>;
  },
};

function ChakraUIRenderer(theme?: Defaults, merge = true): Components {
  const elements = {
    p: defaults.p,
    em: defaults.em,
    strong: defaults.strong,
    blockquote: defaults.blockquote,
    code: defaults.code,
    del: defaults.del,
    hr: defaults.hr,
    a: defaults.a,
    img: defaults.img,
    text: defaults.text,
    ul: defaults.ul,
    ol: defaults.ol,
    li: defaults.li,
    h1: defaults.heading,
    h2: defaults.heading,
    h3: defaults.heading,
    h4: defaults.heading,
    h5: defaults.heading,
    h6: defaults.heading,
    pre: defaults.pre,
    table: defaults.table,
    thead: defaults.thead,
    tbody: defaults.tbody,
    tr: defaults.tr,
    td: defaults.td,
    th: defaults.th,
  };

  if (theme && merge) {
    return deepmerge(elements, theme);
  }

  return elements;
}

export default ChakraUIRenderer;
